import type { ReactElement } from "react";
import { ICON_ART_VISIBLE, badgeArtUnit } from "odyssey-shared";
import { ArtIcon, type IconArt } from "./ArtIcon.tsx";
import { logical } from "./metrics.ts";

/**
 * The staff badge (ADR-112 decisions 4.2, 4.3 and 4.7): the icon a staff member wears before a name, drawn as the
 * pack's colour art at every UI scale (whole device pixels where its visible 24 fit the box, downscaled and smoothed to
 * the box where they do not). There is no glyph look: the art is always what is drawn. A leader draws no frame (the
 * author's ruling of 2026-10-16): its art is the pack's gold set, which tells it from a member's. The type's word is its
 * accessible name and its hover line. Display-only: it takes no pointer events beyond the hover line.
 */

/**
 * The boxes a badge is drawn in, in logical pixels: the built chat line's (8), the design's chat line (10), the name plate's and the target frame's (12, the height of the art's visible 24 pixels at the plate's smallest scales and the most the name's 13 line holds) and the larger boxes of the gallery (13, 16).
 */
export type StaffBadgeBox = 8 | 10 | 12 | 13 | 16;

/**
 * The CSS length of one art pixel of a badge's art in a box (ADR-112 decision 4.3): k device pixels where a whole number
 * of them fits the art's visible 24 in the box, the fraction that fits it where none does. Written against `--px` and the
 * scale, since a device pixel is `--px` over the scale whatever the device pixel ratio.
 *
 * @param box The badge's box.
 * @param scale The UI scale.
 * @returns A `calc` length.
 */
export function badgeArtLength(box: StaffBadgeBox, scale: number): string {
  const { unit, whole } = badgeArtUnit(box, scale);
  return whole ? `calc(var(--px) * ${unit} / ${scale})` : `calc(var(--px) * ${box} / ${ICON_ART_VISIBLE})`;
}

/**
 * The horizontal margin that takes the art's transparent room away, so what shows stands at the badge's own edge: the
 * art's centring in its box and its four transparent pixels each side. A length, in the DOM's own units, so it is a whole
 * number of device pixels at any ratio where the art is.
 *
 * @param box The badge's box.
 * @param scale The UI scale.
 * @returns The margin.
 */
function trimOf(box: StaffBadgeBox, scale: number): string {
  return `calc(${-(box / 2)} * var(--px) + ${ICON_ART_VISIBLE / 2} * ${badgeArtLength(box, scale)})`;
}

/**
 * A staff badge.
 *
 * @param props The `name`, the type's word; the `box`; the UI `scale` the art is sized for; the `art` to draw; and
 *   `tight`, which takes the badge's transparent room away on both sides so it stands as close to what is beside it as
 *   that stands to its own neighbours (P1.9-T122).
 * @returns The badge, an image named by its word.
 * @example
 * <StaffBadge name="Player Support" box={12} scale={2} art={art} />
 */
export function StaffBadge({ name, box, scale, art, tight = false }: { readonly name: string; readonly box: StaffBadgeBox; readonly scale: number; readonly art: IconArt; readonly tight?: boolean }): ReactElement {
  const { whole } = badgeArtUnit(box, scale);
  return (
    <span className="ot-staff-badge" role="img" aria-label={name} data-kit="staff-badge" style={{ width: logical(box), height: logical(box), ...(tight ? { marginLeft: trimOf(box, scale), marginRight: trimOf(box, scale) } : {}) }}>
      <ArtIcon art={art} room={box} at={{ left: 0, top: 0 }} unit={badgeArtLength(box, scale)} scaled={!whole} />
    </span>
  );
}
